/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import MuiTabular from 'modules/common/components/mui-tabular';

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { Card, Typography, Grid, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CollapseIcon from 'modules/common/components/collapse-icon';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTargetDate,
  selectWorkbookDateType,
} from 'modules/dashboard/selectors';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';

import GetAppIcon from '@mui/icons-material/GetApp';
import { useDebouncedCallback } from 'use-debounce';
import { ENVIRONMENT } from 'config';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import { AssistantIcon } from 'modules/common/components';
import { Box } from '@mui/system';
import {
  RATE_CATEGORY_COLUMNS,
  RATE_COLUMN_INFO_MAP,
  RATE_CATEGORY_TABLE_COLUMNS,
} from '../../../../constants';
import {
  buildRateCategoryFilters,
  buildRatePlanHeaderData,
  handleCeruleanLogging,
} from '../../../../function';
import {
  CARD_CONTENT_STYLE,
  TABULAR_CARD_STYLE,
  RATE_HEADER_STYLE,
  RATE_ROW_STYLE,
  EXPORT_PDF_BUTTON_STYLE,
  exportPdfTextStyle,
} from '../../../../style';

import {
  selectDateComparison,
  selectRatePlanData,
  selectSymmetricComparisonToggle,
  selectWorkbookAlignmentToggle,
  selectWorkbookBookingDate,
  selectWorkbookEndDate,
  selectWorkbookStartDate,
} from '../../../../selectors';
import ColumnInfo from '../column-info/column-info';
import { workbookActions } from '../../../../slice';

const CeruleanRateCategoryVisual = ({
  isDisplay,
  onClick,
  title,
  enableSecondaryHotelGroups = false,
}) => {
  const bookingDate = useSelector(selectWorkbookBookingDate);
  const pms = useSelector(selectPmsSync);
  const targetDate = useSelector(selectTargetDate);
  const selectedDateType = useSelector(selectWorkbookDateType);
  const organizationId = useSelector(selectOrganizationId);
  const hotels = useSelector(selectHotelName);
  const alignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const dateComparison = useSelector(selectDateComparison);
  const currencyType = useSelector(selectLocalCurrency);
  const endDate = useSelector(selectWorkbookEndDate);
  const startDate = useSelector(selectWorkbookStartDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const ratePlanData = useSelector(selectRatePlanData);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const dispatch = useDispatch();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  // const [ratePlanData, setData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  //
  const tableRef = useRef();
  //
  const fetchData = useDebouncedCallback(async () => {
    let start;
    // Build the filters for the API request
    const filters = buildRateCategoryFilters({
      bookingDate,
      targetDate,
      dateComparison,
      pms,
      hotels,
      alignmentToggle,
      symmetricComparisonToggle,
      currencyType,
      startDate,
      endDate,
      resetBookingDate,
      arrival: selectedDateType?.id === 'ARRIVAL' ? 'arrival_date' : 'stay_date',
      partialHours,
      lastBookingDate,
    });
    try {
      setIsLoading(true);
      // Log filter payload
      handleCeruleanLogging({
        message: `Rate Category Stay Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      // Log request start
      handleCeruleanLogging({
        message: `Rate Category Stay Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      setExportData({
        columns: RATE_CATEGORY_COLUMNS,
        filters,
        title: 'cerulean-rate-category-visual',
      });
      dispatch(
        workbookActions.getRatePlanData({
          organizationId,
          columns: RATE_CATEGORY_COLUMNS,
          filters,
        })
      );
    } catch (error) {
      handleCeruleanLogging({
        message: `Error fetching Rate Category Stay tabular ratePlanData: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      const end = performance.now();

      handleCeruleanLogging({
        message: `Rate Category Stay Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });

      handleCeruleanLogging({
        message: `Rate Category Stay Tabular Request time: ${((end - start) / 1000).toFixed(
          2
        )} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    if (!isDisplay) {
      return;
    }
    fetchData();
  }, [
    isDisplay,
    selectedDateType,
    pms,
    targetDate,
    bookingDate,
    hotels,
    currencyType,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    startDate,
    endDate,
    lastBookingDate,
    partialHours,
  ]);

  useEffect(() => {
    if (ratePlanData && isLoading) {
      setIsLoading(false);
    }
  }, [ratePlanData]);

  const headerData = useMemo(
    () =>
      buildRatePlanHeaderData({
        bookingDate,
        targetDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        selectedDateType: selectedDateType?.id,
        dateComparison,
        startDate,
        endDate,
        resetBookingDate,
        lastBookingDate,
        partialHours,
      }),
    [
      bookingDate,
      targetDate,
      alignmentToggle,
      selectedDateType?.id,
      symmetricComparisonToggle,
      currencyType,
      dateComparison,
      startDate,
      endDate,
      resetBookingDate,
      partialHours,
      lastBookingDate,
    ]
  );
  //
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      className={styles.mainGrid}
      mb={10}
    >
      <Card sx={TABULAR_CARD_STYLE}>
        <Box
          display="flex"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={40}
          pl={2}
        >
          <Typography>{title}</Typography>
          <Box display="flex" alignItems="center">
            {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
              <Grid item mt={1} mr={4}>
                <AssistantIcon
                  exportData={exportData}
                  visualName={title}
                  isProcessing={isExporting}
                  setIsProcessing={setIsExporting}
                  disabled={isProcessing || isLoading || isExporting || !isDisplay}
                  isCerulean
                  enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                  getExcelExportData={tableRef?.current?.getExcelData}
                  exportType={exportType}
                  setExportType={setExportType}
                />
              </Grid>
            )}
            <>
              <Typography sx={exportPdfTextStyle(isLoading)}>Export Data</Typography>
              <IconButton
                size="small"
                onClick={() => setIsProcessing(true)}
                disabled={isLoading}
                sx={EXPORT_PDF_BUTTON_STYLE}
              >
                <GetAppIcon />
              </IconButton>
            </>
            <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
          </Box>
        </Box>
        {isDisplay && (
          <Grid sx={CARD_CONTENT_STYLE}>
            {!ratePlanData ? (
              <Grid container justifyContent="center" alignItems="center" m={5}>
                <Typography fontWeight={500}>Loading Data...</Typography>
              </Grid>
            ) : (
              <>
                <MuiTabular
                  ref={tableRef}
                  columns={RATE_CATEGORY_TABLE_COLUMNS}
                  data={ratePlanData}
                  isAggregate={false}
                  isProcessing={isProcessing}
                  isLoadingData={isLoading}
                  setIsProcessing={setIsProcessing}
                  headerComponent={
                    <Grid container spacing={2} mt={1} pl={1}>
                      {headerData?.map((section, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`${section?.id}-${index.toString()}`}>
                          <Typography
                            fontSize={10.5}
                            fontFamily="'Segoe UI', wf_segoe-ui_normal, helvetica, arial, sans-serif"
                          >
                            {section?.title}: {section?.value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  }
                  headerFiltersInfo={{ title, sections: headerData }}
                  fileName="Rate_Category"
                  customHeaderStyle={RATE_HEADER_STYLE}
                  customRowStyle={RATE_ROW_STYLE}
                  primaryTextColor="#000"
                  secondaryTextColor="#000"
                />
                <ColumnInfo columnInfoMap={RATE_COLUMN_INFO_MAP} />
              </>
            )}
          </Grid>
        )}
      </Card>
    </Grid>
  );
};

export default CeruleanRateCategoryVisual;
